import { useRef, useEffect, useCallback } from "react";
import type { ResonanceFrame } from "@shared/types/resonance";

interface ResonanceOrbProps {
  frame: ResonanceFrame;
  size?: number;
}

const WARM = [255, 180, 40] as const;
const WARM_HOT = [255, 130, 20] as const;
const COOL = [80, 150, 255] as const;
const COOL_DEEP = [40, 90, 230] as const;
const NEUTRAL = [100, 100, 160] as const;

function lerp(a: readonly number[], b: readonly number[], t: number): string {
  return `rgb(${Math.round(a[0] + (b[0] - a[0]) * t)},${Math.round(a[1] + (b[1] - a[1]) * t)},${Math.round(a[2] + (b[2] - a[2]) * t)})`;
}

function lerpRGBA(a: readonly number[], b: readonly number[], t: number, alpha: number): string {
  return `rgba(${Math.round(a[0] + (b[0] - a[0]) * t)},${Math.round(a[1] + (b[1] - a[1]) * t)},${Math.round(a[2] + (b[2] - a[2]) * t)},${alpha})`;
}

export function ResonanceOrb({ frame, size = 48 }: ResonanceOrbProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const timeRef = useRef(0);

  const draw = useCallback((ctx: CanvasRenderingContext2D, s: number, t: number) => {
    ctx.clearRect(0, 0, s, s);

    const cx = s / 2;
    const cy = s / 2;
    const baseR = s * 0.32;

    const warmCount = frame.nodes.filter(n => n.g === 1).length;
    const coolCount = frame.nodes.filter(n => n.g === -1).length;
    const total = frame.nodes.length || 1;
    const warmRatio = warmCount / total;
    const coolRatio = coolCount / total;
    const dominantWarm = warmRatio >= coolRatio;

    const primaryColor = dominantWarm ? WARM : COOL;
    const secondaryColor = dominantWarm ? WARM_HOT : COOL_DEEP;
    const intensity = Math.max(warmRatio, coolRatio, 0.3);

    const breathe = frame.frozen ? 1 : 1 + Math.sin(t * 1.8) * 0.04;
    const pulse = frame.frozen ? 0 : Math.sin(t * 3.0) * 0.03;
    const orbR = baseR * breathe;

    const outerGlow = ctx.createRadialGradient(cx, cy, orbR * 0.3, cx, cy, orbR * 2.2);
    outerGlow.addColorStop(0, lerpRGBA(primaryColor, secondaryColor, intensity, 0.15 + intensity * 0.1));
    outerGlow.addColorStop(0.4, lerpRGBA(primaryColor, secondaryColor, intensity * 0.5, 0.08));
    outerGlow.addColorStop(1, "transparent");
    ctx.fillStyle = outerGlow;
    ctx.fillRect(0, 0, s, s);

    const midGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, orbR * 1.5);
    midGlow.addColorStop(0, lerpRGBA(primaryColor, secondaryColor, intensity, 0.4 + pulse));
    midGlow.addColorStop(0.5, lerpRGBA(primaryColor, secondaryColor, intensity * 0.6, 0.15));
    midGlow.addColorStop(1, "transparent");
    ctx.fillStyle = midGlow;
    ctx.beginPath();
    ctx.arc(cx, cy, orbR * 1.5, 0, Math.PI * 2);
    ctx.fill();

    const coreGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, orbR);
    coreGrad.addColorStop(0, lerp([255, 255, 255], primaryColor, 0.3));
    coreGrad.addColorStop(0.3, lerp(primaryColor, secondaryColor, intensity));
    coreGrad.addColorStop(0.7, lerpRGBA(secondaryColor, NEUTRAL, 0.3, 0.6));
    coreGrad.addColorStop(1, "transparent");
    ctx.fillStyle = coreGrad;
    ctx.globalAlpha = 0.85 + pulse;
    ctx.beginPath();
    ctx.arc(cx, cy, orbR, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;

    const avgActivation = frame.nodes.length > 0
      ? frame.nodes.reduce((sum, n) => sum + n.a, 0) / frame.nodes.length
      : 0;

    const nodeCount = Math.min(frame.nodes.length, 12);
    for (let i = 0; i < nodeCount; i++) {
      const n = frame.nodes[i];
      const angle = (i / nodeCount) * Math.PI * 2 + (frame.frozen ? 0 : t * 0.3);
      const dist = orbR * (0.3 + n.a * 0.5);
      const nx = cx + Math.cos(angle) * dist;
      const ny = cy + Math.sin(angle) * dist;
      const nr = 1 + n.a * 2.5;

      const nodeCol = n.g === 1 ? WARM : n.g === -1 ? COOL : NEUTRAL;
      const nodeGlow = ctx.createRadialGradient(nx, ny, 0, nx, ny, nr * 3);
      nodeGlow.addColorStop(0, lerpRGBA(nodeCol, [255, 255, 255], 0.3, n.a * 0.7));
      nodeGlow.addColorStop(1, "transparent");
      ctx.fillStyle = nodeGlow;
      ctx.fillRect(nx - nr * 3, ny - nr * 3, nr * 6, nr * 6);

      ctx.globalAlpha = 0.5 + n.a * 0.5;
      ctx.beginPath();
      ctx.arc(nx, ny, nr, 0, Math.PI * 2);
      ctx.fillStyle = lerp(NEUTRAL, nodeCol, n.a);
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    const innerHighlight = ctx.createRadialGradient(cx - orbR * 0.15, cy - orbR * 0.2, 0, cx, cy, orbR * 0.6);
    innerHighlight.addColorStop(0, "rgba(255,255,255,0.25)");
    innerHighlight.addColorStop(1, "transparent");
    ctx.fillStyle = innerHighlight;
    ctx.beginPath();
    ctx.arc(cx, cy, orbR * 0.6, 0, Math.PI * 2);
    ctx.fill();

    if (frame.coherence > 0) {
      const pct = `${Math.round(frame.coherence * 100)}`;
      ctx.save();
      ctx.font = `bold ${Math.round(s * 0.18)}px system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      ctx.shadowColor = dominantWarm ? "rgba(255,180,40,0.6)" : "rgba(80,150,255,0.6)";
      ctx.shadowBlur = 4;
      ctx.fillStyle = "rgba(255,255,255,0.9)";
      ctx.fillText(pct, cx, cy);
      ctx.shadowBlur = 0;

      ctx.font = `${Math.round(s * 0.1)}px system-ui, sans-serif`;
      ctx.fillStyle = "rgba(255,255,255,0.5)";
      ctx.fillText("%", cx + ctx.measureText(pct).width * 0.35, cy - s * 0.06);
      ctx.restore();
    }

    if (!frame.frozen && avgActivation > 0.2) {
      const ringAlpha = 0.1 + Math.sin(t * 2) * 0.05;
      ctx.globalAlpha = ringAlpha;
      ctx.strokeStyle = lerp(primaryColor, secondaryColor, 0.5);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(cx, cy, orbR * 1.3 + Math.sin(t * 1.5) * 2, 0, Math.PI * 2);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
  }, [frame]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio, 2);
    const s = size;
    canvas.width = s * dpr;
    canvas.height = s * dpr;
    canvas.style.width = `${s}px`;
    canvas.style.height = `${s}px`;
    ctx.scale(dpr, dpr);

    if (frame.frozen) {
      draw(ctx, s, 0);
      return;
    }

    let running = true;
    const animate = () => {
      if (!running) return;
      timeRef.current += 0.016;
      draw(ctx, s, timeRef.current);
      animRef.current = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      running = false;
      cancelAnimationFrame(animRef.current);
    };
  }, [frame, size, draw]);

  return (
    <canvas
      ref={canvasRef}
      className="block"
      style={{ width: size, height: size }}
      data-testid="canvas-resonance-orb"
    />
  );
}
